package com.sddamico.components.widgets

import androidx.compose.runtime.*
import com.sddamico.SitePalette
import com.sddamico.api.ApiException
import com.sddamico.api.apiJson
import com.sddamico.api.apiPost
import com.sddamico.models.ExecuteRosterMoveRequest
import com.sddamico.models.RosterMoveResponse
import com.sddamico.models.RosterPlayerResponse
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

@Composable
fun LivDeactivateModal(
    player: RosterPlayerResponse,
    clubId: String,
    onClose: () -> Unit,
    onSuccess: () -> Unit,
) {
    val scope = rememberCoroutineScope()

    var submitting by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    Modal(onDismiss = onClose) {
        Column(Modifier.fillMaxWidth().gap(1.25.cssRem)) {
            // Header
            Row(Modifier.fillMaxWidth().gap(0.5.cssRem), verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    "Move to LIV Line",
                    Modifier.fontSize(1.25.cssRem).fontWeight(FontWeight.Bold).color(SitePalette.text)
                )
                Spacer()
                SportBadge(player.sport, player.isLiv)
            }

            // Description
            SpanText(
                "Are you sure you want to move ${player.name} to the LIV line?",
                Modifier.fontSize(0.875.cssRem).color(SitePalette.textSecondary)
            )

            // Error message
            if (errorMessage != null) {
                Div(
                    Modifier
                        .fillMaxWidth()
                        .backgroundColor(Color("#fef2f2"))
                        .border(1.px, LineStyle.Solid, Color("#fca5a5"))
                        .borderRadius(6.px)
                        .padding(0.75.cssRem)
                        .toAttrs()
                ) {
                    SpanText(errorMessage!!, Modifier.color(Color("#991b1b")).fontSize(0.875.cssRem))
                }
            }

            // Buttons
            Row(Modifier.fillMaxWidth().gap(0.75.cssRem), verticalAlignment = Alignment.CenterVertically) {
                Spacer()

                // No button
                Div(
                    Modifier
                        .backgroundColor(SitePalette.border)
                        .color(SitePalette.text)
                        .borderRadius(8.px)
                        .padding(topBottom = 0.5.cssRem, leftRight = 1.cssRem)
                        .fontSize(0.875.cssRem)
                        .fontWeight(FontWeight.Medium)
                        .cursor(Cursor.Pointer)
                        .onClick { dismiss(onClose) }
                        .toAttrs()
                ) {
                    Text("No")
                }

                // Yes button
                Div(
                    Modifier
                        .backgroundColor(if (!submitting) SitePalette.error else SitePalette.border)
                        .color(if (!submitting) Colors.White else SitePalette.textMuted)
                        .borderRadius(8.px)
                        .padding(topBottom = 0.5.cssRem, leftRight = 1.cssRem)
                        .fontSize(0.875.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .cursor(if (!submitting) Cursor.Pointer else Cursor.Default)
                        .onClick {
                            if (submitting) return@onClick
                            scope.launch {
                                submitting = true
                                errorMessage = null
                                try {
                                    val body = apiJson.encodeToString(
                                        ExecuteRosterMoveRequest(
                                            moveType = "ROSTER_SWAP",
                                            clubId = clubId,
                                            rosterSlot = player.rosterSlot,
                                            toLivLine = true,
                                        )
                                    )
                                    apiPost("/api/rostermoves/execute", body, RosterMoveResponse.serializer())
                                    dismiss(onSuccess)
                                } catch (e: ApiException) {
                                    errorMessage = e.errorMessage
                                } catch (_: Exception) {
                                    errorMessage = "Failed to move player to LIV Line"
                                }
                                submitting = false
                            }
                        }
                        .toAttrs()
                ) {
                    Text(if (submitting) "Moving..." else "Yes")
                }
            }
        }
    }
}
